/**
 * The shell: chrome, routing, and the three places sync gets kicked.
 *
 * Everything that decides anything lives elsewhere — `SyncEngine` decides sync state,
 * `FieldRepository` decides what is stored, `status-text.ts` decides what the banner says.
 * What is left here is composition, on purpose: this is the hardest file to test, so it
 * should be the one with the least in it.
 *
 * `app` is optional, and that is not defensive habit. The component renders under plain Node
 * in CI (`App.test.ts`) where there is no IndexedDB to open, and the deployed page must still
 * show which commit is live even if local storage refuses to open on a locked-down phone. A
 * missing app degrades to readable chrome, never to a blank screen.
 */

import { useEffect, useState } from 'react';

import { formatBuildLabel, type BuildInfo } from './build-info.js';
import type { FieldApp } from './bootstrap.js';
import type { NewJobInput } from './db/repository.js';
import { Banner } from './ui/components.js';
import { useAsync, useSyncSnapshot } from './ui/hooks.js';
import { InspectionScreen } from './ui/InspectionScreen.js';
import { JobListScreen } from './ui/JobListScreen.js';
import { NewJobScreen } from './ui/NewJobScreen.js';
import { formatBytes, syncBanner } from './ui/status-text.js';
import { styles } from './ui/styles.js';

/**
 * How often to retry the queue while the app is open.
 *
 * Thirty seconds is a compromise ADR-0001 §4.8 forces: iOS gives us no reliable background
 * sync, so the foreground is the only window we can count on. Frequent enough that walking
 * past the truck's hotspot drains the queue, rare enough not to become a battery complaint
 * on a ten-hour storm day.
 */
const SYNC_INTERVAL_MS = 30_000;

type Route =
  | { readonly name: 'list' }
  | { readonly name: 'new' }
  | { readonly name: 'job'; readonly jobId: string };

export function App({ build, app }: { build: BuildInfo; app?: FieldApp | undefined }) {
  const [route, setRoute] = useState<Route>({ name: 'list' });
  const snapshot = useSyncSnapshot(app?.sync);

  // Three triggers, one drain. `syncNow` coalesces, so these cannot stack up.
  useEffect(() => {
    if (app === undefined) return;
    const { sync } = app;

    void sync.refresh().then(async () => await sync.syncNow());

    const onConnectivity = () => {
      void sync.handleConnectivityChange();
    };
    window.addEventListener('online', onConnectivity);
    window.addEventListener('offline', onConnectivity);

    const timer = setInterval(() => {
      void sync.syncNow();
    }, SYNC_INTERVAL_MS);

    return () => {
      window.removeEventListener('online', onConnectivity);
      window.removeEventListener('offline', onConnectivity);
      clearInterval(timer);
    };
  }, [app]);

  const jobs = useAsync(
    async () => (app === undefined ? undefined : await app.repository.jobSummaries()),
    [app, route.name],
  );

  const jobId = route.name === 'job' ? route.jobId : undefined;
  const view = useAsync(
    async () =>
      app === undefined || jobId === undefined ? undefined : await app.repository.getJob(jobId),
    [app, jobId],
  );
  const statuses = useAsync(
    async () =>
      app === undefined || jobId === undefined
        ? undefined
        : await app.repository.photoStatuses(jobId),
    [app, jobId],
  );

  const createJob = async (input: NewJobInput): Promise<void> => {
    if (app === undefined) throw new Error('Local storage is unavailable on this device.');
    const job = await app.repository.createJob(input);
    // Straight into the checklist. The rep opened this form to take photos, not to admire a
    // list, and the job is durable at this point whether or not sync ever runs.
    setRoute({ name: 'job', jobId: job.jobId });
    await app.sync.refresh();
    void app.sync.syncNow();
  };

  const capture = async (slotId: string, bytes: Uint8Array, contentType: string): Promise<void> => {
    if (app === undefined || jobId === undefined) return;
    // Awaited: this is the local write, and it is the part that must not be lost.
    await app.repository.capture({ jobId, slotId, bytes, contentType });

    view.reload();
    statuses.reload();
    // Counts come from local storage, so "N waiting" moves the instant the photo lands
    // rather than waiting on a round trip that may never happen.
    await app.sync.refresh();
    // Deliberately not awaited. ADR-0001 §4.6: capture completes at the local write. A rep
    // on a roof with one bar must never wait on an upload to take the next shot.
    void app.sync.syncNow();
  };

  return (
    <div style={styles.app}>
      <header style={styles.header}>
        <h1 style={styles.title}>RUSH Field</h1>
      </header>

      <Banner status={syncBanner(snapshot)} />

      <div style={styles.body}>
        {app === undefined ? (
          <p style={styles.cardMeta}>
            Local storage is unavailable on this device, so capture is turned off. Nothing has
            been lost — anything captured earlier is still stored and will upload once this is
            fixed.
          </p>
        ) : route.name === 'new' ? (
          <NewJobScreen
            onCreate={createJob}
            onCancel={() => {
              setRoute({ name: 'list' });
            }}
          />
        ) : route.name === 'job' ? (
          view.value === undefined ? (
            <p style={styles.cardMeta}>
              {view.loading ? 'Opening inspection…' : 'That inspection is not on this device.'}
            </p>
          ) : (
            <InspectionScreen
              repository={app.repository}
              view={view.value}
              statuses={statuses.value ?? new Map()}
              onCapture={capture}
              onBack={() => {
                setRoute({ name: 'list' });
              }}
            />
          )
        ) : (
          <JobListScreen
            jobs={jobs.value}
            loading={jobs.loading}
            onOpen={(id) => {
              setRoute({ name: 'job', jobId: id });
            }}
            onNew={() => {
              setRoute({ name: 'new' });
            }}
          />
        )}
      </div>

      <footer style={styles.footer}>
        {/* Photos held locally is the number that predicts the next outage: a phone that
            cannot write is a phone that silently stops capturing evidence. */}
        <div>
          {snapshot.counts.photosHeldLocally > 0
            ? `${String(snapshot.counts.photosHeldLocally)} photo${snapshot.counts.photosHeldLocally === 1 ? '' : 's'} on this device · ${formatBytes(snapshot.counts.bytesHeldLocally)}`
            : 'No photos held on this device'}
        </div>
        {/* The human-readable copy of the deployed commit — what you compare in a browser to
            see a rollback take effect. CI does not scrape it: the markup ships an empty #root
            and fills it in on the client, so the smoke test asserts against the static
            /version.json instead. Both come from the same build variables. */}
        <div data-testid="build-label">{formatBuildLabel(build)}</div>
      </footer>
    </div>
  );
}
